import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import {
  type ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeuix/themes';
import {
  provideTanStackQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { providePrimeNG } from 'primeng/config';
import type { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { routes } from './app.routes';

export const globalModalConfig: DynamicDialogConfig = {
  appendTo: 'body',
  draggable: false,
  resizable: false,
  styleClass:
    '[&_.p-dialog-content]:contents! [&_.p-dialog-content]:overflow-hidden! [&_.p-dialog-content>*]:contents',
  dismissableMask: false,
  closable: true,
  closeOnEscape: false,
  focusOnShow: true,
  showHeader: false,
  width: '550px',
  breakpoints: {
    '960px': '75vw',
    '640px': '90vw',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    // ! required for app-menu component
    provideAnimations(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      }),
    ),
    provideHttpClient(),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    providePrimeNG({
      theme: {
        preset: definePreset(Aura, {
          semantic: {
            primary: {
              50: '{teal.50}',
              100: '{teal.100}',
              200: '{teal.200}',
              300: '{teal.300}',
              400: '{teal.400}',
              500: '{teal.700}',
              600: '{teal.600}',
              700: '{teal.700}',
              800: '{teal.800}',
              900: '{teal.900}',
              950: '{teal.950}',
            },
          },
        }),
        options: {
          darkModeSelector: '.theme-dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    }),
    provideHttpClient(),
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            // Structural sharing to prevent unnecessary re-renders and deduplication
            structuralSharing: true,
            // Default stale time to reduce unnecessary refetches
            staleTime: 0,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
          },
        },
      }),
      withDevtools(),
    ),
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { dateFormat: 'shortDate', timezone: 'it' },
    },
  ],
};
